import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Tab,
  Tabs,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { SectionCard } from "./SectionCard";
import { SettingsSection } from "./SettingsSection";
import type { ClientManagementState } from "../types";
import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import type { ClientDepartment, ClientJobRole } from "../api/clientManagement";

interface DefaultsTabProps {
  managementState: ClientManagementState;
  onChange: (field: keyof ClientManagementState, value: string | boolean) => void;
  defaultDepartmentsQuery: UseQueryResult<{ data: ClientDepartment[] }>;
  createDefaultDepartmentMutation: UseMutationResult<
    unknown,
    unknown,
    { name: string; is_active: boolean }
  >;
  updateDefaultDepartmentMutation: UseMutationResult<
    unknown,
    unknown,
    { departmentId: string; payload: { name?: string; is_active?: boolean } }
  >;
  deleteDefaultDepartmentMutation: UseMutationResult<unknown, unknown, string>;
  defaultJobRolesQuery: UseQueryResult<{ data: ClientJobRole[] }>;
  createDefaultJobRoleMutation: UseMutationResult<
    unknown,
    unknown,
    { name: string; is_active: boolean }
  >;
  updateDefaultJobRoleMutation: UseMutationResult<
    unknown,
    unknown,
    { jobRoleId: string; payload: { name?: string; is_active?: boolean } }
  >;
  deleteDefaultJobRoleMutation: UseMutationResult<unknown, unknown, string>;
}

export const DefaultsTab: React.FC<DefaultsTabProps> = ({
  managementState,
  onChange,
  defaultDepartmentsQuery,
  createDefaultDepartmentMutation,
  updateDefaultDepartmentMutation,
  deleteDefaultDepartmentMutation,
  defaultJobRolesQuery,
  createDefaultJobRoleMutation,
  updateDefaultJobRoleMutation,
  deleteDefaultJobRoleMutation,
}) => {
  const [departmentEdits, setDepartmentEdits] = useState<
    Record<string, { name: string; is_active: boolean }>
  >({});
  const [jobRoleEdits, setJobRoleEdits] = useState<
    Record<string, { name: string; is_active: boolean }>
  >({});
  const [activeSection, setActiveSection] = useState<
    "departments" | "job-roles"
  >("departments");
  const [departmentModalOpen, setDepartmentModalOpen] = useState(false);
  const [jobRoleModalOpen, setJobRoleModalOpen] = useState(false);
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));

  useEffect(() => {
    if (!defaultDepartmentsQuery.data?.data?.length) return;
    setDepartmentEdits((prev) => {
      const next = { ...prev };
      defaultDepartmentsQuery.data?.data.forEach((department) => {
        if (!next[department.id]) {
          next[department.id] = {
            name: department.name,
            is_active: department.is_active !== false,
          };
        }
      });
      return next;
    });
  }, [defaultDepartmentsQuery.data]);

  useEffect(() => {
    if (!defaultJobRolesQuery.data?.data?.length) return;
    setJobRoleEdits((prev) => {
      const next = { ...prev };
      defaultJobRolesQuery.data?.data.forEach((role) => {
        if (!next[role.id]) {
          next[role.id] = {
            name: role.name,
            is_active: role.is_active !== false,
          };
        }
      });
      return next;
    });
  }, [defaultJobRolesQuery.data]);

  return (
    <SettingsSection
      title="Defaults"
      description="Default departments and job roles used when new clients are created."
    >
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 3 }}>
          <SectionCard
            title="Defaults menu"
            description="Choose which defaults to edit."
          >
            <Tabs
              orientation={isMdUp ? "vertical" : "horizontal"}
              value={activeSection}
              onChange={(_, value) => setActiveSection(value)}
              variant={isMdUp ? "fullWidth" : "scrollable"}
              aria-label="Defaults navigation"
            >
              <Tab value="departments" label="Departments" />
              <Tab value="job-roles" label="Job roles" />
            </Tabs>
          </SectionCard>
        </Grid>
        <Grid size={{ xs: 12, md: 9 }}>
          {activeSection === "departments" && (
            <SectionCard
              title="Default departments"
              description="Manage the default departments."
            >
              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <Box display="flex" justifyContent="flex-end">
                    <Button
                      variant="contained"
                      onClick={() => setDepartmentModalOpen(true)}
                    >
                      Add department
                    </Button>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  {defaultDepartmentsQuery.isLoading ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CircularProgress size={18} />
                      <Typography variant="body2">
                        Loading default departments...
                      </Typography>
                    </Stack>
                  ) : defaultDepartmentsQuery.data?.data?.length ? (
                    <TableContainer sx={{ width: "100%" }}>
                      <Table size="small" sx={{ width: "100%" }}>
                        <TableHead>
                          <TableRow>
                            <TableCell>Department</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {defaultDepartmentsQuery.data.data.map((department) => {
                            const edit = departmentEdits[department.id] ?? {
                              name: department.name,
                              is_active: department.is_active !== false,
                            };
                            const hasChanges =
                              edit.name !== department.name ||
                              edit.is_active !== (department.is_active !== false);
                            return (
                              <TableRow key={department.id} sx={{ "& td": { py: 1.5 } }}>
                                <TableCell>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    label="Department name"
                                    value={edit.name}
                                    onChange={(event) =>
                                      setDepartmentEdits((prev) => ({
                                        ...prev,
                                        [department.id]: {
                                          ...edit,
                                          name: event.target.value,
                                        },
                                      }))
                                    }
                                  />
                                </TableCell>
                                <TableCell>
                                  <FormControlLabel
                                    control={
                                      <Switch
                                        checked={edit.is_active}
                                        onChange={(event) =>
                                          setDepartmentEdits((prev) => ({
                                            ...prev,
                                            [department.id]: {
                                              ...edit,
                                              is_active: event.target.checked,
                                            },
                                          }))
                                        }
                                      />
                                    }
                                    label={edit.is_active ? "Active" : "Inactive"}
                                  />
                                </TableCell>
                                <TableCell align="right">
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    justifyContent="flex-end"
                                  >
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      disabled={
                                        !hasChanges ||
                                        updateDefaultDepartmentMutation.isPending
                                      }
                                      onClick={() =>
                                        updateDefaultDepartmentMutation.mutate({
                                          departmentId: department.id,
                                          payload: {
                                            name: edit.name,
                                            is_active: edit.is_active,
                                          },
                                        })
                                      }
                                    >
                                      Save
                                    </Button>
                                    <Button
                                      color="error"
                                      variant="text"
                                      size="small"
                                      disabled={
                                        deleteDefaultDepartmentMutation.isPending
                                      }
                                      onClick={() => {
                                        if (
                                          window.confirm(
                                            "Delete this default department? This cannot be undone."
                                          )
                                        ) {
                                          deleteDefaultDepartmentMutation.mutate(
                                            department.id
                                          );
                                        }
                                      }}
                                    >
                                      Delete
                                    </Button>
                                  </Stack>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No default departments found yet.
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </SectionCard>
          )}

          {activeSection === "job-roles" && (
            <SectionCard
              title="Default job roles"
              description="Manage the default job roles."
            >
              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <Box display="flex" justifyContent="flex-end">
                    <Button
                      variant="contained"
                      onClick={() => setJobRoleModalOpen(true)}
                    >
                      Add job role
                    </Button>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  {defaultJobRolesQuery.isLoading ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CircularProgress size={18} />
                      <Typography variant="body2">
                        Loading default job roles...
                      </Typography>
                    </Stack>
                  ) : defaultJobRolesQuery.data?.data?.length ? (
                    <TableContainer sx={{ width: "100%" }}>
                      <Table size="small" sx={{ width: "100%" }}>
                        <TableHead>
                          <TableRow>
                            <TableCell>Job role</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {defaultJobRolesQuery.data.data.map((role) => {
                            const edit = jobRoleEdits[role.id] ?? {
                              name: role.name,
                              is_active: role.is_active !== false,
                            };
                            const hasChanges =
                              edit.name !== role.name ||
                              edit.is_active !== (role.is_active !== false);
                            return (
                              <TableRow key={role.id} sx={{ "& td": { py: 1.5 } }}>
                                <TableCell>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    label="Job role name"
                                    value={edit.name}
                                    onChange={(event) =>
                                      setJobRoleEdits((prev) => ({
                                        ...prev,
                                        [role.id]: {
                                          ...edit,
                                          name: event.target.value,
                                        },
                                      }))
                                    }
                                  />
                                </TableCell>
                                <TableCell>
                                  <FormControlLabel
                                    control={
                                      <Switch
                                        checked={edit.is_active}
                                        onChange={(event) =>
                                          setJobRoleEdits((prev) => ({
                                            ...prev,
                                            [role.id]: {
                                              ...edit,
                                              is_active: event.target.checked,
                                            },
                                          }))
                                        }
                                      />
                                    }
                                    label={edit.is_active ? "Active" : "Inactive"}
                                  />
                                </TableCell>
                                <TableCell align="right">
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    justifyContent="flex-end"
                                  >
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      disabled={
                                        !hasChanges ||
                                        updateDefaultJobRoleMutation.isPending
                                      }
                                      onClick={() =>
                                        updateDefaultJobRoleMutation.mutate({
                                          jobRoleId: role.id,
                                          payload: {
                                            name: edit.name,
                                            is_active: edit.is_active,
                                          },
                                        })
                                      }
                                    >
                                      Save
                                    </Button>
                                    <Button
                                      color="error"
                                      variant="text"
                                      size="small"
                                      disabled={
                                        deleteDefaultJobRoleMutation.isPending
                                      }
                                      onClick={() => {
                                        if (
                                          window.confirm(
                                            "Delete this default job role? This cannot be undone."
                                          )
                                        ) {
                                          deleteDefaultJobRoleMutation.mutate(
                                            role.id
                                          );
                                        }
                                      }}
                                    >
                                      Delete
                                    </Button>
                                  </Stack>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No default job roles found yet.
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </SectionCard>
          )}
        </Grid>
      </Grid>

      <Dialog
        open={departmentModalOpen}
        onClose={() => setDepartmentModalOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add default department</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              autoFocus
              fullWidth
              label="Department name"
              size="small"
              value={managementState.departmentName}
              onChange={(event) =>
                onChange("departmentName", event.target.value)
              }
            />
            <FormControlLabel
              control={
                <Switch
                  checked={managementState.departmentActive}
                  onChange={(event) =>
                    onChange("departmentActive", event.target.checked)
                  }
                />
              }
              label="Active"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            variant="text"
            onClick={() => setDepartmentModalOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={
              createDefaultDepartmentMutation.isPending ||
              !managementState.departmentName.trim()
            }
            onClick={() =>
              createDefaultDepartmentMutation.mutate(
                {
                  name: managementState.departmentName.trim(),
                  is_active: managementState.departmentActive,
                },
                {
                  onSuccess: () => setDepartmentModalOpen(false),
                }
              )
            }
          >
            Add department
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={jobRoleModalOpen}
        onClose={() => setJobRoleModalOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add default job role</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              autoFocus
              fullWidth
              label="Job role name"
              size="small"
              value={managementState.jobRoleName}
              onChange={(event) => onChange("jobRoleName", event.target.value)}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={managementState.jobRoleActive}
                  onChange={(event) =>
                    onChange("jobRoleActive", event.target.checked)
                  }
                />
              }
              label="Active"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setJobRoleModalOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={
              createDefaultJobRoleMutation.isPending ||
              !managementState.jobRoleName.trim()
            }
            onClick={() =>
              createDefaultJobRoleMutation.mutate(
                {
                  name: managementState.jobRoleName.trim(),
                  is_active: managementState.jobRoleActive,
                },
                {
                  onSuccess: () => setJobRoleModalOpen(false),
                }
              )
            }
          >
            Add job role
          </Button>
        </DialogActions>
      </Dialog>
    </SettingsSection>
  );
};
